import { z } from 'zod';
import { ComponentHealthStatusSchema } from './fleet.js';
export const PROHIBITED_PHI_TELEMETRY_KEYS = [
    'patientname',
    'patient_name',
    'mrn',
    'patientid',
    'patient_id',
    'diagnosis',
    'prescription',
    'clinicalnotes',
    'clinical_notes',
    'doctornotes',
    'doctor_notes',
    'aadhaar',
    'aadhar',
    'mobile',
    'phonenumber',
    'phone_number',
    'dob',
    'dateofbirth',
    'date_of_birth',
    'address',
    'ssn',
    'bloodgroup',
    'blood_group',
    'medicalhistory',
    'medical_history',
    'treatment_plan',
    'vitals_patient',
];
/**
 * Minimal on-prem telemetry payload: contractually approved aggregates and system health only.
 * Strictly non-PHI.
 */
export const OnPremTelemetryPayloadSchema = z
    .object({
    schemaVersion: z.literal('onprem.v1'),
    installedVersion: z.string(),
    installationIdentifier: z.string(),
    uptimeSeconds: z.number().int().nonnegative(),
    components: z.record(z.string(), z.object({
        status: ComponentHealthStatusSchema,
        latencyMs: z.number().nonnegative().optional(),
        message: z.string().optional(),
    })),
    operationalMetrics: z
        .object({
        activeDoctorsCount: z.number().int().nonnegative(),
        licensedBedsOccupied: z.number().int().nonnegative(),
        monthlyOpdVisitsCount: z.number().int().nonnegative(),
        connectedDevicesCount: z.number().int().nonnegative(),
        storageGbUsed: z.number().nonnegative(),
        dbConnectionsCount: z.number().int().nonnegative(),
        queueDepth: z.number().int().nonnegative(),
        errorRatePercent: z.number().min(0).max(100),
    })
        .strict(),
    backupFreshnessHours: z.number().nonnegative().nullable().optional(),
    lastBackupVerifiedAt: z.iso.datetime().nullable().optional(),
})
    .strict();
/**
 * Cloud managed telemetry payload: infrastructure health and load metrics.
 */
export const CloudTelemetryPayloadSchema = z
    .object({
    schemaVersion: z.literal('cloud.v1'),
    installedVersion: z.string(),
    installationIdentifier: z.string(),
    region: z.string(),
    components: z.record(z.string(), z.object({
        status: ComponentHealthStatusSchema,
        latencyMs: z.number().nonnegative().optional(),
        errorRate: z.number().min(0).max(100).optional(),
    })),
    systemMetrics: z
        .object({
        cpuPercent: z.number().min(0).max(100),
        memoryPercent: z.number().min(0).max(100),
        diskUsagePercent: z.number().min(0).max(100),
        requestsPerSecond: z.number().nonnegative(),
        p95LatencyMs: z.number().nonnegative(),
        errorRatePercent: z.number().min(0).max(100),
    })
        .strict(),
    operationalMetrics: z
        .object({
        activeDoctorsCount: z.number().int().nonnegative(),
        licensedBedsOccupied: z.number().int().nonnegative(),
        monthlyOpdVisitsCount: z.number().int().nonnegative(),
        connectedDevicesCount: z.number().int().nonnegative(),
        storageGbUsed: z.number().nonnegative(),
    })
        .strict(),
})
    .strict();
/**
 * Signed Heartbeat envelope sent by remote installations.
 */
export const SignedHeartbeatEnvelopeSchema = z
    .object({
    installationIdentifier: z.string(),
    keyId: z.string(),
    timestamp: z.iso.datetime(),
    nonce: z.string().min(8).max(64),
    signature: z.string().min(32),
    payload: z.union([OnPremTelemetryPayloadSchema, CloudTelemetryPayloadSchema]),
})
    .strict();
/**
 * Validates that an object contains no forbidden PHI or clinical keys recursively.
 */
export function scanForForbiddenPhiKeys(data, path = '') {
    const violations = [];
    if (!data || typeof data !== 'object')
        return violations;
    if (Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
            violations.push(...scanForForbiddenPhiKeys(data[i], `${path}[${i}]`));
        }
        return violations;
    }
    for (const [key, value] of Object.entries(data)) {
        const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
        for (const prohibited of PROHIBITED_PHI_TELEMETRY_KEYS) {
            const normalizedProhibited = prohibited.replace(/[^a-z0-9]/g, '');
            if (normalizedKey === normalizedProhibited || normalizedKey.includes(normalizedProhibited)) {
                violations.push(`${path ? `${path}.` : ''}${key}`);
            }
        }
        if (value && typeof value === 'object') {
            violations.push(...scanForForbiddenPhiKeys(value, path ? `${path}.${key}` : key));
        }
    }
    return violations;
}
/**
 * Deterministic JSON with sorted object keys.
 *
 * `JSON.stringify` preserves insertion order, so two structurally identical
 * payloads can serialise to different bytes and produce different HMACs. Any
 * value that is signed or verified must go through this instead.
 */
export function canonicalJson(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    const entries = Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
}
/**
 * The exact string a heartbeat signature is computed over.
 *
 * It binds the installation, key id, timestamp and nonce to the payload —
 * mirroring the Stage 1 webhook scheme (`timestamp.nonce.body`). Signing the
 * payload alone would let a captured heartbeat be replayed with a fresh nonce
 * inside the timestamp window, because the nonce would not be covered by the
 * signature.
 */
export function heartbeatSigningString(envelope) {
    return [
        envelope.installationIdentifier,
        envelope.keyId,
        envelope.timestamp,
        envelope.nonce,
        canonicalJson(envelope.payload),
    ].join('.');
}
//# sourceMappingURL=telemetry.js.map